import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

// ── DTOs ────────────────────────────────────────────────────────────

export interface AvanceFinancieroDTO {
  total_anticipos: number;
  total_proforma: number;
  total_ejecutado_proforma: number;
  porcentaje_ejecucion_proforma: number;
  total_pagos_maestros: number;
  total_despachos_valor: number;
  total_consumido: number;
  porcentaje_consumido: number;
  alert_level: 'none' | 'critica' | 'maxima';
  costo_insumos_adicionales: number;
}

export interface AvanceFisicoItemDTO {
  proforma_item_id: string;
  descripcion: string;
  cantidad_proformada: number;
  cantidad_ejecutada: number;
  porcentaje_avance: number;
}

export interface AvanceFisicoCategoryDTO {
  categoria: string;
  promedio_ponderado: number;
  items: AvanceFisicoItemDTO[];
}

export interface AvanceFisicoGroupedDTO {
  categories: AvanceFisicoCategoryDTO[];
}

// ── Hooks ───────────────────────────────────────────────────────────

export function useAvanceFinanciero(projectId: string) {
  return useQuery({
    queryKey: queryKeys.avance.financiero(projectId),
    queryFn: async () => {
      const res = await api.get<{ data: AvanceFinancieroDTO }>(`/projects/${projectId}/avance`);
      return res.data.data;
    },
    enabled: !!projectId,
  });
}

export function useAvanceFisico(projectId: string) {
  return useQuery({
    queryKey: queryKeys.avance.fisico(projectId),
    queryFn: async () => {
      const res = await api.get<{ data: AvanceFisicoGroupedDTO }>(`/projects/${projectId}/avance/fisico`);
      return res.data.data;
    },
    enabled: !!projectId,
  });
}

export function useAvancePdfUrl(projectId: string) {
  // Returns the URL to fetch the avance PDF/HTML preview
  return `/projects/${projectId}/avance/pdf`;
}

// ── Reporte Financiero ──────────────────────────────────────────────

export interface ReporteItemDTO {
  codigo: string;
  descripcion: string;
  unidad: string;
  precio_unitario: number;
  cantidad_proformada: number;
  cantidad_periodo: number;
  total_periodo: number;
  cantidad_acumulado: number;
  total_acumulado: number;
  cantidad_pendiente: number;
  total_pendiente: number;
}

export interface ReporteAdicionalDTO {
  codigo: string;
  descripcion: string;
  unidad: string;
  precio_unitario: number;
  cantidad_periodo: number;
  monto_periodo: number;
  cantidad_acumulado: number;
  monto_acumulado: number;
  es_servicio_varios?: boolean;
}

export interface ReporteServicioVarioDTO {
  descripcion: string;
  categoria: string;
  cantidad_periodo: number;
  monto_periodo: number;
  cantidad_acumulado: number;
  monto_acumulado: number;
}

export interface ReportePagoDTO {
  fecha: string;
  monto: number;
  notas: string | null;
}

export interface AvanceReporteDTO {
  proyecto_nombre: string;
  proyecto_codigo: string;
  cliente_nombre: string;
  fecha_desde: string | null;
  fecha_hasta: string | null;
  iva_rate: number;
  items: ReporteItemDTO[];
  adicionales: ReporteAdicionalDTO[];
  servicios_varios: ReporteServicioVarioDTO[];
  subtotal_periodo: number;
  adicionales_periodo: number;
  servicios_varios_periodo: number;
  total_sin_iva_periodo: number;
  iva_periodo: number;
  total_periodo: number;
  subtotal_acumulado: number;
  adicionales_acumulado: number;
  servicios_varios_acumulado: number;
  total_sin_iva_acumulado: number;
  iva_acumulado: number;
  total_acumulado: number;
  descuento: number;
  subtotal_proforma: number;
  total_sin_iva_proforma: number;
  total_proforma: number;
  subtotal_pendiente: number;
  pendiente_por_cobrar: number;
  pagos: ReportePagoDTO[];
  total_pagado: number;
  saldo_pendiente: number;
}

export function useAvanceReporte(projectId: string, desde?: string, hasta?: string) {
  return useQuery({
    queryKey: [...queryKeys.avance.financiero(projectId), 'reporte', desde, hasta],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      const url = `/projects/${projectId}/avance/reporte${params.toString() ? '?' + params.toString() : ''}`;
      const res = await api.get<{ data: AvanceReporteDTO }>(url);
      return res.data.data;
    },
    enabled: !!projectId,
  });
}

export async function downloadAvanceXlsx(
  projectId: string,
  projectCodigo: string,
  desde?: string,
  hasta?: string,
): Promise<void> {
  const params = new URLSearchParams();
  if (desde) params.set('desde', desde);
  if (hasta) params.set('hasta', hasta);
  const url = `/projects/${projectId}/avance/xlsx${params.toString() ? '?' + params.toString() : ''}`;
  const res = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `avance-${projectCodigo}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
}
